// @flow

import * as React from 'react';
import styled from '@emotion/styled';

import Columns from 'app/components/commons/Columns';
import Icon from 'app/components/commons/Icon';
import Loader from 'app/components/commons/Loader';
import Section from 'app/components/commons/Section';
import { getPrizes } from 'app/api/h2h';
import { PriceButton } from './components';

type Props = {
  contestType: string,
  entryFee: number,
  minEntries: number,
  maxEntries: number,
  numEntries: number,
  selectedPrizeStructure: string,
  didFetch: (Array<*>) => void
};

const PrizeIcon = styled(({ className }) => (
  <Icon type="icon-trophy" size={20} className={className} />
))`
  color: ${({ theme }) => theme.color.primary};
  margin-right: 6px;
  vertical-align: baseline;
`;

const PrizeLabel = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  textTransform: 'uppercase',
  fontSize: theme.fontSize.default
}));

const PrizeCircle = styled.span(({ theme }) => ({
  borderBottom: `2px solid ${theme.color.primary}`,
  marginRight: theme.spacing.xsmall
}));

const PrizeValue = styled.span(({ theme }) => ({
  fontSize: theme.fontSize.medium
}));

const PrizesSection: React.FC<Props> = ({
  contestType,
  entryFee,
  maxEntries,
  minEntries,
  numEntries,
  selectedPrizeStructure,
  didFetch
}: Props) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [prizes, setPrizes] = React.useState([]);

  const fetchPrizes = () => {
    if (contestType !== 'multi_players' || (numEntries >= minEntries && numEntries <= maxEntries)) {
      setIsLoading(true);
      getPrizes({
        type: contestType,
        total_fee: entryFee,
        maximum_entries: contestType === 'multi_players' ? numEntries : undefined,
        pay_structure: contestType === 'multi_players' ? selectedPrizeStructure : undefined
      })
        .then(({ data }) => {
          setPrizes(data);
          didFetch(data);
          setIsLoading(false);
        })
        .catch(() => {
          setPrizes([]);
          didFetch([]);
          setIsLoading(false);
        });
    }
  };

  // Get prizes when contest type, entry fee, prize structure or numEntries changes
  React.useEffect(fetchPrizes, [contestType, entryFee, selectedPrizeStructure, numEntries]);

  return (
    <Section title="prizes">
      <PrizesWrapper>
        {prizes.length > 0 &&
          !isLoading &&
          prizes.map(({ position, label }) => (
            <div key={position}>
              <PriceButton>
                <PrizeLabel>
                  <PrizeIcon />
                  <PrizeCircle>{position}</PrizeCircle>
                  <PrizeValue>{label}</PrizeValue>
                </PrizeLabel>
              </PriceButton>
            </div>
          ))}
        {isLoading && <Loader pad />}
      </PrizesWrapper>
    </Section>
  );
};

export default PrizesSection;

const PrizesWrapper = styled.div`
  @media screen and (min-width: 769px) {
    display: flex;
    flex-wrap: wrap;
  }

  > div {
    flex: 1;
    padding: 5px;
  }
`;
